import { toast } from "../../../../toast";
import React, { useEffect, useState, useMemo } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { Table } from "flowbite-react";
import AdminLayout from "../../Layout/index.jsx";
import Loader from "../../../../components/Loader.jsx";
import Pagination from "../../../../components/Pagination/index.jsx";
import ConformationModal from "../../Components/ConformationModal.jsx";
import {
  useDeleteCronJobsMutation,
  useGetAllAssignmentsQuery,
  DateFilter,
} from "../../../../graphql/generated.tsx";

const GET_ALL_STAFFS_WITH_ROLE = gql`
  query GetAllStaffsWithRole {
    getAllStaffs {
      id
      email
      isAdminStaff
    }
  }
`;

const TRIGGER_CRON_REPORTS = gql`
  mutation TriggerCronReports($jobIds: [String!]!) {
    triggerCronReports(jobIds: $jobIds)
  }
`;

const GET_ALL_CRON_JOBS = gql`
  query GetAllCronJobsWithAssignment($input: GetAllJobsInput!) {
    getAllCronJobs(input: $input) {
      data {
        id
        job_id
        emailId
        campusName
        assignmentId
        status
        createdAt
      }
      total
    }
  }
`;

const ITEMS_PER_PAGE = 20;

const DATE_RANGE_TO_API = {
  ALL: DateFilter.TillDate,
  "15D": DateFilter.Last_15Days,
  "1M": DateFilter.Last_1Month,
  CUSTOM: DateFilter.CustomDate,
};

const StatusBadge = ({ status }) => {
  const s = status?.toUpperCase();
  if (!s || s === "REJECTED") {
    return (
      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">
        Rejected
      </span>
    );
  }
  if (s === "COMPLETED") {
    return (
      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
        Completed
      </span>
    );
  }
  if (s === "PENDING") {
    return (
      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
        Pending
      </span>
    );
  }
  return (
    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
      {status}
    </span>
  );
};

const Cronlog = () => {
  const [Isload, setIsload] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState("PENDING");
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState("15D");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeMode, setActiveMode] = useState(null); // null | "delete" | "trigger"
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTriggerModal, setShowTriggerModal] = useState(false);

  const apiInput = useMemo(() => {
    const input = {
      limit: ITEMS_PER_PAGE,
      skip: (currentPage - 1) * ITEMS_PER_PAGE,
      dateFilter: DATE_RANGE_TO_API[dateRange] || DateFilter.TillDate,
    };
    if (dateRange === "CUSTOM" && customFrom) input.startDate = customFrom;
    if (dateRange === "CUSTOM" && customTo) input.endDate = customTo;
    return input;
  }, [currentPage, dateRange, customFrom, customTo]);

  const { data, error, loading, refetch } = useQuery(GET_ALL_CRON_JOBS, {
    variables: { input: apiInput },
    fetchPolicy: "network-only",
  });
  const [deleteCronJobs, { loading: deleting }] = useDeleteCronJobsMutation();
  const [triggerCronReports, { loading: triggering }] = useMutation(TRIGGER_CRON_REPORTS);

  const { data: staffsData } = useQuery(GET_ALL_STAFFS_WITH_ROLE);
  const { data: assignmentsData } = useGetAllAssignmentsQuery();

  const assignmentNameMap = useMemo(() => {
    const map = {};
    (assignmentsData?.getAllAssignments ?? []).forEach((a) => {
      map[a.id] = a.name;
    });
    return map;
  }, [assignmentsData]);

  const adminEmailSet = useMemo(() => {
    const emails = (staffsData?.getAllStaffs ?? [])
      .filter((s) => s.isAdminStaff === true)
      .map((s) => s.email?.toLowerCase());
    return new Set(emails);
  }, [staffsData]);

  useEffect(() => {
    setTimeout(() => {
      setIsload(false);
    }, 2000);
  }, []);

  if (Isload)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );

  if (error)
    return (
      <AdminLayout>
        <p className="text-red-500 p-4">Error loading cron jobs: {error.message}</p>
      </AdminLayout>
    );

  const allJobs = data?.getAllCronJobs?.data ?? [];
  const totalFromApi = data?.getAllCronJobs?.total ?? 0;

  // Status filter — frontend only
  const statusFiltered =
    filterStatus === "ALL"
      ? allJobs
      : filterStatus === "REJECTED"
      ? allJobs.filter((j) => !j.status || j.status?.toUpperCase() === "REJECTED")
      : allJobs.filter((j) => j.status?.toUpperCase() === filterStatus);

  // Search — frontend only (on current page data)
  const filtered = searchText.trim()
    ? statusFiltered.filter((j) => {
        const campus = (j.campusName ?? "").toLowerCase();
        const email = (j.emailId ?? "").toLowerCase();
        const q = searchText.toLowerCase();
        return campus.includes(q) || email.includes(q);
      })
    : statusFiltered;

  const totalPages = Math.ceil(totalFromApi / ITEMS_PER_PAGE);

  const resetPage = () => setCurrentPage(1);

  // IDs eligible based on active mode
  const eligibleIds = activeMode === "trigger"
    ? filtered.filter((j) => j.status?.toUpperCase() === "PENDING").map((j) => j.id)
    : filtered.map((j) => j.id);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allEligibleSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    if (allEligibleSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        eligibleIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        eligibleIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const cancelMode = () => {
    setActiveMode(null);
    setSelectedIds(new Set());
  };

  const confirmDelete = async () => {
    try {
      await deleteCronJobs({ variables: { jobIds: Array.from(selectedIds) } });
      setSelectedIds(new Set());
      setActiveMode(null);
      toast.success("Cron job(s) deleted successfully");
      refetch();
    } catch (err) {
      toast.error(err?.message || "Failed to delete cron job(s). Please try again.");
    }
  };

  const confirmTrigger = async () => {
    try {
      const jobUuids = filtered
        .filter((j) => selectedIds.has(j.id))
        .map((j) => j.job_id);
      await triggerCronReports({ variables: { jobIds: jobUuids } });
      setSelectedIds(new Set());
      setActiveMode(null);
      toast.success("Approval email sent successfully");
      refetch();
    } catch (err) {
      toast.error(err?.message || "Failed to send approval email(s). Please try again.");
    }
  };

  return (
    <AdminLayout>
      <ConformationModal
        openModal={showDeleteModal}
        setOpenModal={setShowDeleteModal}
        event={confirmDelete}
        text={`Are you sure you want to delete ${selectedIds.size} selected cron job(s)?`}
      />
      <ConformationModal
        openModal={showTriggerModal}
        setOpenModal={setShowTriggerModal}
        event={confirmTrigger}
        sidebarMode="submit"
        heading="Send Approval Email"
        subtitle="This will send an approval email for the selected pending cron job(s)."
        primaryButtonText="Send Email"
        confirmTitle="Confirm email"
        text={`Are you sure you want to send an approval email for ${selectedIds.size} pending cron job(s)?`}
      />
      <div className="p-6 bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="flex flex-col gap-3 mb-6 md:flex-row md:items-center md:justify-between">
          <h2 className="text-2xl font-bold text-gray-800">Cron Job Logs</h2>

          <div className="flex flex-wrap items-center gap-3">
            {/* Search — frontend only */}
            <input
              type="text"
              placeholder="Search campus / email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="px-3 py-2 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
            />

            {/* Date range — sent to API */}
            <select
              value={dateRange}
              onChange={(e) => { setDateRange(e.target.value); resetPage(); }}
              className="px-3 py-2 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">Till Date</option>
              <option value="15D">Last 15 Days</option>
              <option value="1M">Last 1 Month</option>
              <option value="CUSTOM">Custom Date</option>
            </select>

            {/* Custom date pickers */}
            {dateRange === "CUSTOM" && (
              <>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => { setCustomFrom(e.target.value); resetPage(); }}
                  className="px-3 py-2 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => { setCustomTo(e.target.value); resetPage(); }}
                  className="px-3 py-2 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
              </>
            )}

            {/* Status filter — frontend only */}
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); resetPage(); }}
              className="px-3 py-2 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="COMPLETED">Completed</option>
              <option value="REJECTED">Rejected</option>
            </select>

            {/* Trigger Mail button */}
            {activeMode !== "delete" && (
              <button
                onClick={() => {
                  if (activeMode === "trigger") {
                    if (selectedIds.size > 0) setShowTriggerModal(true);
                  } else {
                    setActiveMode("trigger");
                    setSelectedIds(new Set());
                  }
                }}
                disabled={triggering || (activeMode === "trigger" && selectedIds.size === 0)}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                  activeMode === "trigger" && selectedIds.size > 0
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : activeMode === "trigger"
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-green-600 text-white hover:bg-green-700"
                }`}
              >
                {triggering
                  ? "Sending..."
                  : activeMode === "trigger"
                  ? `Confirm Send${selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}`
                  : "Send Approval Email"}
              </button>
            )}

            {/* Delete button */}
            {activeMode !== "trigger" && (
              <button
                onClick={() => {
                  if (activeMode === "delete") {
                    if (selectedIds.size > 0) setShowDeleteModal(true);
                  } else {
                    setActiveMode("delete");
                    setSelectedIds(new Set());
                  }
                }}
                disabled={deleting || (activeMode === "delete" && selectedIds.size === 0)}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                  activeMode === "delete" && selectedIds.size > 0
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : activeMode === "delete"
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-red-600 text-white hover:bg-red-700"
                }`}
              >
                {deleting
                  ? "Deleting..."
                  : activeMode === "delete"
                  ? `Confirm Delete${selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}`
                  : "Delete"}
              </button>
            )}

            {/* Cancel button — shown when any mode is active */}
            {activeMode && (
              <button
                onClick={cancelMode}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <Table hoverable>
            <Table.Head>
              <Table.HeadCell>#</Table.HeadCell>
              <Table.HeadCell>Campus Name</Table.HeadCell>
              <Table.HeadCell>Email Address</Table.HeadCell>
              <Table.HeadCell>Role</Table.HeadCell>
              <Table.HeadCell>Assignment Name</Table.HeadCell>
              <Table.HeadCell>Created At</Table.HeadCell>
              <Table.HeadCell>Status</Table.HeadCell>
              {activeMode && (
                <Table.HeadCell>
                  <input
                    type="checkbox"
                    checked={allEligibleSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 cursor-pointer accent-blue-600"
                    title="Select all eligible on this page"
                  />
                </Table.HeadCell>
              )}
            </Table.Head>
            <Table.Body className="divide-y">
              {filtered.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={activeMode ? 8 : 7} className="text-center text-gray-500 py-6">
                    {loading ? "Loading..." : "No cron jobs found."}
                  </Table.Cell>
                </Table.Row>
              ) : (
                filtered.map((job, index) => (
                  <Table.Row key={job.id} className="bg-white dark:bg-gray-800">
                    <Table.Cell>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</Table.Cell>
                    <Table.Cell>{job.campusName}</Table.Cell>
                    <Table.Cell>{job.emailId ?? "-"}</Table.Cell>
                    <Table.Cell>
                      {adminEmailSet.has((job.emailId ?? "").toLowerCase()) ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">Admin</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">Staff</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>{assignmentNameMap[job.assignmentId] ?? "-"}</Table.Cell>
                    <Table.Cell>
                      {job.createdAt
                        ? new Date(job.createdAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "-"}
                    </Table.Cell>
                    <Table.Cell>
                      <StatusBadge status={job.status} />
                    </Table.Cell>
                    {activeMode && (
                      <Table.Cell>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(job.id)}
                          onChange={() => toggleSelect(job.id)}
                          disabled={activeMode === "trigger" && job.status?.toUpperCase() !== "PENDING"}
                          className={`w-4 h-4 accent-blue-600 ${
                            activeMode === "trigger" && job.status?.toUpperCase() !== "PENDING"
                              ? "opacity-30 cursor-not-allowed"
                              : "cursor-pointer"
                          }`}
                        />
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
    </AdminLayout>
  );
};

export default Cronlog;
